import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle } from "lucide-react";

const PAIN_COLOR = {
  tightness:  "#FCD34D",
  soreness:   "#FB923C",
  discomfort: "#F87171",
  sharp_pain: "#DC2626",
};

/**
 * Body Scan Heatmap (Coach Player Detail).
 *
 * 14-day historical aggregate: SVG silhouette + zone dots scaled by severity,
 * click a zone → side panel showing every flag event in the window.
 */
export default function BodyScanHeatmap({ playerId, days = 14 }) {
  const [data, setData] = useState(null);
  const [activeZone, setActiveZone] = useState(null);

  useEffect(() => {
    if (!playerId) return;
    api.get(`/players/${playerId}/body-scan-history?days=${days}`).then(({ data }) => setData(data));
  }, [playerId, days]);

  if (!data) return null;

  if (data.total_flags === 0) {
    return (
      <div className="rounded-xl ink-card p-5" data-testid="body-scan-heatmap">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Body Scan · last {data.days}d</div>
        <h3 className="font-display font-bold text-lg text-white mt-1">No flagged zones</h3>
        <p className="text-xs text-white/50 mt-1">Athlete has not logged any pain on the body map in the last {data.days} days.</p>
      </div>
    );
  }

  const maxSeverity = Math.max(...data.zones.map((z) => z.severity), 1);
  const activeEvents = activeZone ? data.events.filter((e) => e.zone_id === activeZone) : [];
  const activeZoneObj = data.zones.find((z) => z.zone_id === activeZone);

  return (
    <div className="rounded-xl ink-card p-5" data-testid="body-scan-heatmap">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Body Scan Heatmap</div>
          <h3 className="font-display font-bold text-lg text-white mt-1">
            {data.total_flags} flag{data.total_flags === 1 ? "" : "s"} · last {data.days}d
          </h3>
        </div>
        {data.zones.some((z) => z.sharp_pain_count > 0) && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
            style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.45)" }}>
            <AlertTriangle className="h-3 w-3" /> {data.zones.reduce((s, z) => s + z.sharp_pain_count, 0)} sharp pain
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-[180px_1fr] gap-4 items-start">
        {/* Silhouette w/ severity-scaled dots */}
        <div className="mx-auto md:mx-0">
          <svg viewBox="0 0 240 520" className="w-40 h-auto block" aria-label="Body heatmap">
            <g fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.2">
              <circle cx="120" cy="48" r="28" />
              <path d="M108 76 L108 92 L132 92 L132 76" />
              <path d="M70 100 L170 100 L182 200 L168 295 L72 295 L58 200 Z" />
              <path d="M70 100 L40 200 L46 260 L60 260 L72 200" />
              <path d="M170 100 L200 200 L194 260 L180 260 L168 200" />
              <path d="M88 295 L78 420 L84 500 L106 500 L112 420 L116 305" />
              <path d="M152 295 L162 420 L156 500 L134 500 L128 420 L124 305" />
            </g>
            {data.zones.map((z) => {
              const ratio = z.severity / maxSeverity;
              const r = 6 + Math.round(ratio * 9);
              const color = PAIN_COLOR[z.max_level] || "#22D3EE";
              const isActive = activeZone === z.zone_id;
              return (
                <g key={z.zone_id} style={{ cursor: "pointer" }} onClick={() => setActiveZone((c) => (c === z.zone_id ? null : z.zone_id))}
                   data-testid={`heatmap-zone-${z.zone_id}`}>
                  <circle cx={z.coords.x} cy={z.coords.y} r={r + 8} fill="transparent" />
                  <circle cx={z.coords.x} cy={z.coords.y} r={r}
                    fill={color} fillOpacity={0.35 + ratio * 0.4}
                    stroke={color} strokeWidth={isActive ? 2.5 : 1.5}
                  />
                  {z.sharp_pain_count > 0 && (
                    <circle cx={z.coords.x} cy={z.coords.y} r={r + 3} fill="none"
                      stroke={PAIN_COLOR.sharp_pain} strokeWidth="1.2" strokeDasharray="2,2">
                      <animate attributeName="r" values={`${r + 3};${r + 7};${r + 3}`} dur="2s" repeatCount="indefinite" />
                    </circle>
                  )}
                </g>
              );
            })}
          </svg>
          <div className="mt-2 flex flex-wrap justify-center gap-1.5 text-[10px]">
            {Object.entries(PAIN_COLOR).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1 text-white/60">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} /> {k.replace("_", " ")}
              </span>
            ))}
          </div>
        </div>

        {/* Zone ranking + drill-down */}
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40 mb-2">Top flagged zones</div>
          <div className="space-y-1.5">
            {data.zones.slice(0, 6).map((z) => {
              const ratio = z.severity / maxSeverity;
              const color = PAIN_COLOR[z.max_level] || "#22D3EE";
              const isActive = activeZone === z.zone_id;
              return (
                <button
                  key={z.zone_id}
                  type="button"
                  onClick={() => setActiveZone((c) => (c === z.zone_id ? null : z.zone_id))}
                  className={`w-full text-left rounded-lg px-3 py-2 transition border ${isActive ? "border-cyan-400/60 bg-cyan-400/5" : "border-white/10 hover:border-white/20 bg-white/[0.02]"}`}
                  data-testid={`heatmap-rank-${z.zone_id}`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="font-semibold text-white truncate">{z.zone_label}</span>
                      {z.sharp_pain_count > 0 && (
                        <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5" }}>
                          {z.sharp_pain_count}× sharp
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-white/50 font-mono-num">{z.count} flag{z.count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full overflow-hidden bg-white/5">
                    <div className="h-full rounded-full" style={{ width: `${ratio * 100}%`, backgroundColor: color }} />
                  </div>
                </button>
              );
            })}
          </div>

          {activeZoneObj && (
            <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="heatmap-drilldown">
              <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/40">Flag timeline · {activeZoneObj.zone_label}</div>
              <div className="mt-2 space-y-1 max-h-44 overflow-y-auto">
                {activeEvents.map((ev, i) => (
                  <div key={`${ev.log_id}-${i}`} className="flex items-center justify-between text-xs">
                    <span className="text-white/70 font-mono-num">{new Date(ev.timestamp).toLocaleDateString()}</span>
                    <span className="font-bold uppercase tracking-wide text-[10px]" style={{ color: PAIN_COLOR[ev.pain_level] }}>
                      {ev.pain_level.replace("_", " ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
